import {
  DeleteJobsBulkDto,
  DeleteScheduledJobsBulkDto,
  PermissionsDto,
  StoppedScheduledDto,
  UploadVideosJobDto,
  UploadVideosJobSummaryDto,
  UploadVideosScheduledJobDto,
  UploadVideosScheduledJobSummaryDto,
  UploadVideosScheduleJobDto,
  UploadVideosUpdateJobDto,
} from "@/client";
import { ExtendedAction, PaginatedResponseDto } from "@/client/extend";
import { FileRouteTypes } from "@/routeTree.gen";
import React from "react";
import { FormItemCommonProps } from "@/components/custom/form-inputs/common";

type ExtractSegment<
  T extends string,
  Prefix extends string,
> = T extends `${Prefix}/${infer Segment}/${string}`
  ? Segment
  : T extends `${Prefix}/${infer Segment}`
    ? Exclude<Segment, "">
    : never;

export type JobSummaryBase = Omit<UploadVideosJobSummaryDto, "data" | "result">;
export type JobSummary<TData, TResult, TInfo = any> = JobSummaryBase & {
  data?: TData;
  result: TResult;
  info?: TInfo;
};
export type JobBase = Omit<UploadVideosJobDto, "data" | "result" | "devices">;
export type Job<TData, TResult> = JobBase & {
  data?: TData;
  result: TResult;
};
export type JobDataWithName<TData, _TResult> = TData & { name?: string };
export type ScheduledJobSummaryBase = Omit<
  UploadVideosScheduledJobSummaryDto,
  "data"
>;
export type ScheduledJobSummary<
  TData,
  _TResult,
  TInfo = any,
> = ScheduledJobSummaryBase & {
  data?: TData;
  info?: TInfo;
};
export type ScheduledJobBase = Omit<UploadVideosScheduledJobDto, "data">;
export type ScheduledJob<TData, _TResult> = ScheduledJobBase & { data?: TData };

export type ScheduleJobBase = Omit<UploadVideosScheduleJobDto, "data">;
export type ScheduleJob<TData, _TResult> = ScheduleJobBase & { data?: TData };

export type UpdateScheduledJobBase = Partial<ScheduleJobBase>;
export type UpdateScheduledJob<TData, _TResult> = UpdateScheduledJobBase & {
  data?: TData;
};

export type UpdateJob = UploadVideosUpdateJobDto;

export type JobActions<TData = any, TResult = any> = {
  createSchedule: ExtendedAction<
    (arg: {
      requestBody: ScheduleJob<TData, TResult>;
    }) => Promise<ScheduledJobSummary<TData, TResult>>
  >;
  updateSchedule: ExtendedAction<
    (arg: {
      id: number;
      partial?: boolean;
      requestBody: UpdateScheduledJob<TData, TResult>;
    }) => Promise<ScheduledJob<TData, TResult>>
  >;
  startScheduled: ExtendedAction<
    (arg: { id: number }) => Promise<Job<TData, TResult>>
  >;
  deleteScheduled: ExtendedAction<(arg: { id: number }) => Promise<boolean>>;
  stopScheduled: ExtendedAction<
    (arg: { id: number }) => Promise<StoppedScheduledDto>
  >;
  resumeScheduled: ExtendedAction<
    (arg: { id: number }) => Promise<ScheduledJobSummary<TData, TResult>>
  >;
  cancelJob: ExtendedAction<(arg: { id: number }) => Promise<boolean>>;
  stopJob: ExtendedAction<(arg: { id: number }) => Promise<boolean>>;
  deleteJob: ExtendedAction<(arg: { id: number }) => Promise<boolean>>;
  /**
   * Bulk actions for the jobs / scheduled jobs table selection. Optional - job types that aren't
   * served by the generic JobController (eg. trainings) don't expose them, and the corresponding
   * bulk action is hidden for those.
   */
  deleteJobsBulk?: ExtendedAction<
    (arg: { requestBody: DeleteJobsBulkDto }) => Promise<number>
  >;
  cancelJobsBulk?: ExtendedAction<
    (arg: { requestBody: DeleteJobsBulkDto }) => Promise<number>
  >;
  deleteScheduledBulk?: ExtendedAction<
    (arg: { requestBody: DeleteScheduledJobsBulkDto }) => Promise<number>
  >;
  cancelScheduledBulk?: ExtendedAction<
    (arg: { requestBody: DeleteScheduledJobsBulkDto }) => Promise<number>
  >;
  stopScheduledBulk?: ExtendedAction<
    (arg: { requestBody: DeleteScheduledJobsBulkDto }) => Promise<number>
  >;
  resumeScheduledBulk?: ExtendedAction<
    (arg: { requestBody: DeleteScheduledJobsBulkDto }) => Promise<number>
  >;
  startJob: ExtendedAction<
    (arg: {
      requestBody: JobDataWithName<TData, TResult>;
    }) => Promise<Job<TData, TResult>>
  >;
  getScheduledJob: ExtendedAction<
    (arg: { id: number }) => Promise<ScheduledJob<TData, TResult>>
  >;
  listScheduled: ExtendedAction<
    (
      arg: {
        page: number;
        limit: number;
      } & any,
    ) => Promise<PaginatedResponseDto<ScheduledJobSummary<TData, TResult>>>
  >;
  getJob: ExtendedAction<(arg: { id: number }) => Promise<Job<TData, TResult>>>;
  updateJob: ExtendedAction<
    (arg: {
      id: number;
      requestBody: UpdateJob;
    }) => Promise<Job<TData, TResult>>
  >;
  listJobs: ExtendedAction<
    (
      arg: {
        page: number;
        limit: number;
      } & any,
    ) => Promise<PaginatedResponseDto<JobSummary<TData, TResult>>>
  >;
};

export type JobInputCommonProps = {
  prefix?: string;
  className?: string;
  container?: FormItemCommonProps["container"];
};

export type JobDefinitionBase = {
  header?: string;
  jobName: keyof PermissionsDto["jobs"];
  basePath?: "/dashboard/jobs" | "/admin-dashboard/jobs";
  path:
    | ExtractSegment<FileRouteTypes["fullPaths"], "/dashboard/jobs">
    | ExtractSegment<FileRouteTypes["fullPaths"], "/admin-dashboard/jobs">;
};

export type JobDefinition<TData = any, TResult = any> = JobDefinitionBase & {
  actions: JobActions<TData, TResult>;
};

export const JobDefinitionContext = React.createContext<JobDefinition>(
  undefined as unknown as JobDefinition,
);
export const useJobDefinition = <
  TJobDefinition extends JobDefinition = JobDefinition,
>() => {
  const ret = React.useContext(JobDefinitionContext) as TJobDefinition;
  return {
    ...ret,
    root: `${ret?.basePath ?? "/dashboard/jobs"}/${ret?.path}` as const,
  };
};
